import React, { useEffect, useState } from 'react';
import { FaTimes, FaUser, FaSearch, FaTrash } from 'react-icons/fa';
import Calling from '../../api/Calling';
import Loader from '../../components/Loader';
import formatDate from '../../components/formatDate';
import generateCaptcha from '../../components/captcha';
import { toast } from 'react-toastify';

const UserReferralHistory = ({ userId, onClose, userName }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [search, setSearch] = useState('');
    const limit = 10;

    const fetchReferrals = async () => {
        setIsLoading(true);
        const response = await Calling.getReferral(page, limit, '', '', '', 1, search, userId);

        if (response.status) {
            setData(response.data);
            setTotalPage(response.totalPage);
            setHasNextPage(response.nextPage);
        }
        setIsLoading(false);
    };

    const handleDelete = async (id) => {
        const content = `You are about to delete this referral. To confirm, please solve this math problem.`;
        const check = generateCaptcha(content);

        if (check) {
            try {
                // Calling.deleteReferral expects array of IDs
                const response = await Calling.deleteReferral([id]);
                if (response?.status) {
                    toast.success(response?.message || 'Referral deleted successfully');
                    fetchReferrals();
                } else {
                    toast.error(response?.message || 'Failed to delete referral');
                }
            } catch (error) {
                console.error('Error deleting referral:', error);
                toast.error('Failed to delete referral');
            }
        } else {
            toast.error("Captcha answer is incorrect", { autoClose: 2000 });
        }
    };

    useEffect(() => {
        if (userId) {
            fetchReferrals();
        }
    }, [userId, page, search]);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col transform transition-all duration-300">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Referral History</h2>
                        <p className="text-sm text-gray-500">Referrals by {userName}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500">
                        <FaTimes size={20} />
                    </button>
                </div>

                {/* Search */}
                <div className='p-4 border-b border-gray-100'>
                    <div className='flex items-center bg-gray-50 rounded-xl px-4 py-2 w-full'>
                        <FaSearch className='text-gray-400 mr-2' />
                        <input
                            type='text'
                            placeholder='Search by Code...'
                            className='bg-transparent outline-none w-full text-sm'
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                    </div>
                </div>

                {/* List */}
                <div className="flex-1 overflow-auto p-4">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-10">
                            <Loader />
                        </div>
                    ) : data.length > 0 ? (
                        <div className="space-y-3">
                            {data.map((item) => (
                                <div key={item._id} className="flex flex-col sm:flex-row items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all gap-4 group">
                                    <div className="flex items-center gap-4 w-full sm:w-auto">
                                        <div className='w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg'>
                                            {item?.u_info?.u_name?.charAt(0).toUpperCase() || <FaUser />}
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-gray-800">{item?.u_info?.u_name || 'N/A'}</h3>
                                            <p className="text-xs text-gray-500">{item?.u_info?.u_email || item?.u_info?.u_device || 'Unknown'}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between w-full sm:w-auto gap-8">
                                        <div className='text-center sm:text-right'>
                                            <p className='text-xs text-gray-400 uppercase font-semibold'>Code</p>
                                            <p className='font-mono font-medium text-blue-600'>{item.r_code}</p>
                                        </div>
                                        <div className='text-center sm:text-right'>
                                            <p className='text-xs text-gray-400 uppercase font-semibold'>Date</p>
                                            <p className='text-sm text-gray-600'>{formatDate(item.createdAt)}</p>
                                        </div>
                                        <button
                                            onClick={() => handleDelete(item._id)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                            title="Delete Referral"
                                        >
                                            <FaTrash size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-gray-500">
                            <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
                                <FaUser className='text-gray-300' size={24} />
                            </div>
                            <p>No referrals found</p>
                        </div>
                    )}
                </div>

                {/* Footer / Pagination */}
                <div className="p-4 border-t border-gray-100 flex justify-between items-center bg-gray-50 rounded-b-2xl">
                    <button
                        disabled={page <= 1}
                        onClick={() => setPage(page - 1)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${page <= 1 ? 'text-gray-400 cursor-not-allowed' : 'bg-white border hover:bg-gray-50 text-gray-600'}`}
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-600">Page {page} of {totalPage || 1}</span>
                    <button
                        disabled={!hasNextPage}
                        onClick={() => setPage(page + 1)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${!hasNextPage ? 'text-gray-400 cursor-not-allowed' : 'bg-white border hover:bg-gray-50 text-gray-600'}`}
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserReferralHistory;
